import { OperacionEstadoAuditListOut } from '../../../../model/operaciones/OperacionEstadoAuditListOut';
import API from '../../../api';
import { ICliente, ICotizador, IProducto, IProveedor, IVoucher } from '../../../api/entity/model';
import { IOperacionAdjunto, IOperacionContacto, IOperacionPasajero, IOperacionUpgrade } from '../../../api/entity/model/IOperacion';
import Security from '../../../seguridad';
import { Estados } from '../Estados';
import { ICotizacionCardEdit } from './ICotizacionCardEdit';
import { ICotizacionInfoCardEdit } from './ICotizacionInfoCardEdit';
import { ICotizacionMensaje } from './ICotizacionMensaje';
import { IOperacionCardEdit } from './IOperacionCardEdit';
import { IOperacionFacturacion } from './IOperacionFacturacion';
import { IOperacionPagoCardEdit } from './IOperacionPagoCardEdit';
import { Response } from "../../../../model";
import { WorkflowEstado } from '../..';
import { Roles } from '../../../api/seguridad/model/IRol';

export type { ICotizacionCardEdit, ICotizacionInfoCardEdit, IOperacionCardEdit, IOperacionPagoCardEdit, ICotizacionMensaje, IOperacionFacturacion };

export type ICardEdit = {
  marca: ICliente;
  cliente: ICliente;
  calcpedido?: any;
  cotizador: ICotizador;
  cotizacion: ICotizacionCardEdit;
  cotizacioninfo?: ICotizacionInfoCardEdit; 
  cotizacionmensajes?: ICotizacionMensaje[];
  operacion?: IOperacionCardEdit;
  
  operacionproducto?: {
    idcalcproducto: number;
    idmoneda: number;
    idproveedor: number;
    proveedor?: IProveedor;
    idproducto: number;
    producto?: IProducto;
    preciomoneda?: string;
    precio?: number;  
    preciousd?: number;  

    /** reing */ idorigen?: number;
    /** reing */ iddestino?: number;
    /** reing */ proveedor_nombre?: string;
    /** reing */ producto_nombre?: string;
    /** reing */ monedacotizacion?: number;
  };

  operacionpago?: IOperacionPagoCardEdit; 
  operacionadjuntos?: IOperacionAdjunto[];
  operacionpasajeros?: IOperacionPasajero[];
  operacioncontactos?: IOperacionContacto[];
  operacionvouchers?: IVoucher[];
  operacionvoucheraudit?: { 
    accion?: string;
    voucherNumero?: string;
    creafecha?:  Date;
    creausulogin?: string;
    cambiofield?: string;
    cambiooldvalue?: string;
    cambionewvalue?: string;
  }[];
  operacionupgrades?: IOperacionUpgrade[];
  operacionfacturacion?: IOperacionFacturacion;
  operacionestados?: OperacionEstadoAuditListOut[];
  operacionproductorecotizacion?: any[];
}

const voucherRelatedFields = ["force", "operacion.fechadesde", "operacion.fechahasta", "operacion.vip", "operacion.pasajeroenviaje", "operacion.pasajeroenviajeorigen", "operacionpasajeros"];

export const CardHelper = {
  estado: (card: ICardEdit | null | undefined) => card?.operacion?.estado ?? card?.cotizacion?.estado,

  /** Indica si se puede modificar el campo según la configuracion */
  allowEdit: (card: ICardEdit | null | undefined, field: string) : boolean => {
    if (!card?.operacion?.estado) return true;
    const relacionadoVoucher = voucherRelatedFields.some(f => f === field);
    if (!relacionadoVoucher) return true;
    
    return ([Estados.Pendiente, Estados.Cobrando].includes(card.operacion.estado) && Security.HasPermiso(222)) 
      || ([Estados.Emitir, Estados.Emitido, Estados.ErrorEmitiendo, Estados.ErrorEnviarVoucher, Estados.VerificarPago, Estados.Facturar].includes(card.operacion.estado) && Security.HasPermiso(223)) 
      || ([Estados.Facturado, Estados.Finalizado].includes(card.operacion.estado) && Security.HasPermiso(224));
  },

  /** Indica si se debe preguntar al usuario si quiere regenerar el voucher */
  mustAskForReemitir: (card: ICardEdit | null | undefined, editedFields: string[]) : boolean => {
    if (!card?.operacion?.estado) return false;
    const hasMatch = editedFields.some(f => voucherRelatedFields.includes(f));
    if (!hasMatch) return false;

    return false
      // || ([Estados.Pendiente, Estados.Cobrando].includes(card.operacion.estado) && Security.HasPermiso(222)) Estos estados no regeneran voucher porque todavía no existe
      || ([Estados.Emitido, Estados.ErrorEmitiendo, Estados.ErrorEnviarVoucher, Estados.VerificarPago, Estados.Facturar].includes(card.operacion.estado) && Security.HasPermiso(223)) 
      || ([Estados.Facturado, Estados.Finalizado].includes(card.operacion.estado) && Security.HasPermiso(224));
  },

  /** Reemite el Voucher si es que está permitido */
  reemitirIfAllow: (card: ICardEdit | null | undefined, editedFields: string[]) : Promise<Response<number>> => {
    const hasMatch = editedFields.some(f => voucherRelatedFields.includes(f));
    if (!hasMatch) return new Promise((resolve) => resolve({ success: true, data: 0 } as Response<number>));
    return API.OperacionesController().Operacion().reemitir({ id: card?.operacion?.id! })
  },

  allowEditCobroField: (card: ICardEdit | null | undefined, field: string) : boolean => {
    const camposLimitados = [
      "operacionpago.fecha", "operacionpago.idmediopago", "operacionpago.idsubmediopago", 
      "operacionpago.monto",
      "operacionpago.cuotas", "operacionpago.cuotasrecargoporc", "operacionpago.cuotasrecargo",
      "operacionpago.descuentocomisionporc", "operacionpago.totaldescuentocomision",
      "operacionpago.totaldescuentovendedorporc", "operacionpago.totaldescuentovendedor", 
      "operacionpago.totalajusteredondeo", "operacionpago.totalajusteredondeousd"
    ];
    const estado = CardHelper.estado(card);
    if (estado === undefined) return false;
    if (!camposLimitados.includes(field)) (  
      // se mantiene lo anterior al CN-165 para los casos no mencionados
      (CardHelper.estado(card) === Estados.Facturar && Security.HasPermiso(225))  // Estado = AFacturar pide permiso 225
      || (CardHelper.estado(card) === Estados.Pendiente)                          // Estado = Pendiente siempre puede cambiar
      || Security.HasPermiso(206)                                                 // Permiso 206 puede cambiar siempre
    );

    const yaEmitido = WorkflowEstado.estadosYaEmitidos.includes(estado);
    const allowAll = Security.HasOneRolOf([Roles.Admin, Roles.Administracion, Roles.Operador]);

    return allowAll || (!yaEmitido);
  }
}